import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { C } from '../constants';
import { factoresTotp, enrolarTotp, verificarTotp, cancelarTotp } from '../supabase';
import { traducirErrorAuth } from '../utils/claveAuth';
import { CapaAtras } from '../utils/capas';

// ═══════════════════════════════════════════════════════════════════════════════
// VERIFICACIÓN EN DOS PASOS — activar TOTP (Lote B2 de la auditoría, nov-2026).
// Se abre desde el menú (Sidebar) para admin y qb. Pasos: 1) escanear el QR con
// la app autenticadora, 2) escribir el código de 6 números. Al verificar, la
// sesión queda en aal2 y en cada entrada se pide el código (MfaCodigo.js).
// Quitarla no se ofrece aquí: lo hace un administrador desde el panel de Auth
// (si se perdió el teléfono). Así un token robado no puede apagarla.
// ═══════════════════════════════════════════════════════════════════════════════

const FUENTE = "'Inter','Segoe UI',sans-serif";

export default function ModalDosPasos({ onCerrar }) {
  const [paso,   setPaso]   = useState('cargando');   // cargando | activa | inicio | qr | lista
  const [qr,     setQr]     = useState(null);         // { factorId, qr, clave }
  const [codigo, setCodigo] = useState('');
  const [err,    setErr]    = useState('');
  const [busy,   setBusy]   = useState(false);

  useEffect(() => {
    let cancel = false;
    factoresTotp()
      .then(({ verificados }) => { if (!cancel) setPaso(verificados.length ? 'activa' : 'inicio'); })
      .catch(e => { if (!cancel) { setErr(traducirErrorAuth(e, 'No pudimos revisar tu cuenta. Intenta de nuevo.')); setPaso('inicio'); } });
    return () => { cancel = true; };
  }, []);

  // Si se cierra a medias, el factor sin verificar se descarta.
  const cerrar = async () => {
    if (busy) return;
    if (paso === 'qr' && qr?.factorId) { try { await cancelarTotp(qr.factorId); } catch { /* se limpia en el próximo intento */ } }
    onCerrar?.();
  };

  const empezar = async () => {
    setErr(''); setBusy(true);
    try {
      setQr(await enrolarTotp());
      setPaso('qr');
    } catch (e) {
      setErr(traducirErrorAuth(e, 'No pudimos generar el código QR. Intenta de nuevo.'));
    } finally { setBusy(false); }
  };

  const verificar = async () => {
    setErr('');
    const limpio = codigo.replace(/\D/g, '');
    if (limpio.length !== 6) { setErr('El código tiene 6 números.'); return; }
    setBusy(true);
    try {
      const { error } = await verificarTotp(qr.factorId, limpio);
      if (error) { setErr(traducirErrorAuth(error, 'No pudimos verificar el código. Intenta de nuevo.')); setCodigo(''); return; }
      setPaso('lista');
    } catch {
      setErr('Error de conexión. Intenta de nuevo.');
    } finally { setBusy(false); }
  };

  const btn = (primario) => ({
    padding: '8px 16px', borderRadius: 8, cursor: busy ? 'default' : 'pointer', fontSize: 12, fontWeight: 700,
    border: primario ? 'none' : `1px solid ${C.border}`, background: primario ? C.teal : '#fff',
    color: primario ? '#fff' : C.muted, opacity: busy ? 0.7 : 1, fontFamily: FUENTE,
  });
  const texto = { fontSize: 12, color: C.navy, lineHeight: 1.5, margin: '0 0 10px' };

  return createPortal(
    <div onClick={cerrar} data-modal="dos-pasos"
      style={{ position: 'fixed', inset: 0, background: 'rgba(13,43,69,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <CapaAtras onCerrar={cerrar} />
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, padding: '22px 22px 20px', width: '100%', maxWidth: 400, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: FUENTE }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 10 }}>Verificación en dos pasos</div>

        {paso === 'cargando' && <p style={texto}>Revisando tu cuenta…</p>}

        {paso === 'activa' && (
          <p style={texto}>
            Ya está activa. Cada vez que entres te pediremos el código de tu app autenticadora.
            Si cambias o pierdes el teléfono, pide a un administrador que la quite y vuelve a activarla.
          </p>
        )}

        {paso === 'inicio' && (
          <>
            <p style={texto}>
              Además de tu contraseña, al entrar te pediremos un código de 6 números que cambia cada 30 segundos.
              Así, aunque alguien sepa tu contraseña, no puede entrar sin tu teléfono.
            </p>
            <p style={texto}>Necesitas una app autenticadora, por ejemplo Google Authenticator o Microsoft Authenticator.</p>
          </>
        )}

        {paso === 'qr' && qr && (
          <>
            <p style={texto}><b>1.</b> En tu app autenticadora, agrega una cuenta y escanea este código.</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0 10px' }}>
              {qr.qr && <img src={qr.qr} alt="Código QR para la app autenticadora" width={180} height={180} />}
            </div>
            <p style={{ ...texto, fontSize: 11, color: C.muted }}>
              Si no puedes escanearlo, escribe esta clave en la app:
              <br /><code style={{ fontSize: 12, wordBreak: 'break-all', color: C.navy }}>{qr.clave}</code>
            </p>
            <p style={texto}><b>2.</b> Escribe el código de 6 números que muestra la app.</p>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={codigo} autoFocus
              aria-label="Código de 6 números" placeholder="000000"
              onChange={e => { setCodigo(e.target.value.replace(/[^\d ]/g, '')); setErr(''); }}
              onKeyDown={e => { if (e.key === 'Enter' && !busy) verificar(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 20, letterSpacing: 6, textAlign: 'center', color: C.navy, fontFamily: FUENTE }} />
          </>
        )}

        {paso === 'lista' && (
          <div style={{ padding: '12px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 8, fontSize: 13, color: '#047857', fontWeight: 600 }}>
            ✓ Verificación en dos pasos activada. La próxima vez que entres te pediremos el código.
          </div>
        )}

        {err && <div style={{ color: C.red, fontSize: 11, marginTop: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          {(paso === 'inicio' || paso === 'qr') && <button onClick={cerrar} disabled={busy} style={btn(false)}>Cancelar</button>}
          {paso === 'inicio' && <button onClick={empezar} disabled={busy} style={btn(true)}>{busy ? 'Preparando…' : 'Activar'}</button>}
          {paso === 'qr' && <button onClick={verificar} disabled={busy} style={btn(true)}>{busy ? 'Verificando…' : 'Verificar'}</button>}
          {(paso === 'activa' || paso === 'lista' || paso === 'cargando') && <button onClick={cerrar} style={btn(true)}>Cerrar</button>}
        </div>
      </div>
    </div>,
    document.body
  );
}
